import { useState, useEffect } from 'react';
import {
  ShieldCheck,
  X,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';
import { checkEmailAuth, EmailAuthDomainResult } from '../../services/api';

interface EmailAuthResultsProps {
  clientId: string;
  onClose?: () => void;
}

type StatusLevel = 'pass' | 'warn' | 'fail';

function StatusIcon({ level }: { level: StatusLevel }) {
  if (level === 'pass') {
    return <CheckCircle className="h-5 w-5 text-green-500" />;
  }
  if (level === 'warn') {
    return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
  }
  return <XCircle className="h-5 w-5 text-red-500" />;
}

function StatusBadge({ level, label }: { level: StatusLevel; label: string }) {
  const colors = {
    pass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    warn: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    fail: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${colors[level]}`}>
      <StatusIcon level={level} />
      {label}
    </span>
  );
}

function getSpfStatus(domain: EmailAuthDomainResult): StatusLevel {
  return domain.spf.found && domain.spf.valid ? 'pass' : 'fail';
}

function getDkimStatus(domain: EmailAuthDomainResult): StatusLevel {
  if (domain.dkim.selector1 && domain.dkim.selector2) return 'pass';
  if (domain.dkim.selector1 || domain.dkim.selector2) return 'warn';
  return 'fail';
}

function getDmarcStatus(domain: EmailAuthDomainResult): StatusLevel {
  if (!domain.dmarc.found) return 'fail';
  if (domain.dmarc.policy === 'reject' || domain.dmarc.policy === 'quarantine') return 'pass';
  if (domain.dmarc.policy === 'none') return 'warn';
  return 'warn';
}

function DomainCard({ domain }: { domain: EmailAuthDomainResult }) {
  const [expanded, setExpanded] = useState(false);

  const spfStatus = getSpfStatus(domain);
  const dkimStatus = getDkimStatus(domain);
  const dmarcStatus = getDmarcStatus(domain);

  const spfLabel = spfStatus === 'pass' ? 'SPF Valid' : 'SPF Missing';
  const dkimLabel = dkimStatus === 'pass' ? 'DKIM OK' : dkimStatus === 'warn' ? 'DKIM Partial' : 'DKIM Missing';
  const dmarcLabel = dmarcStatus === 'pass'
    ? `DMARC ${domain.dmarc.policy}`
    : dmarcStatus === 'warn'
    ? `DMARC ${domain.dmarc.policy || 'monitoring'}`
    : 'DMARC Missing';

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-accent"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <span className="font-medium text-foreground">{domain.domain}</span>
          {domain.isDefault && (
            <span className="text-xs bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 px-2 py-0.5 rounded-full">
              Default
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge level={spfStatus} label={spfLabel} />
          <StatusBadge level={dkimStatus} label={dkimLabel} />
          <StatusBadge level={dmarcStatus} label={dmarcLabel} />
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-gray-400 ml-2" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-400 ml-2" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted p-4 space-y-3">
          {/* SPF */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon level={spfStatus} />
              <span className="text-sm font-medium text-muted-foreground">SPF (Sender Policy Framework)</span>
            </div>
            {domain.spf.found ? (
              <code className="block text-xs bg-muted p-2 rounded font-mono text-muted-foreground break-all">
                {domain.spf.record}
              </code>
            ) : (
              <p className="text-sm text-red-600 dark:text-red-400">
                No SPF record found. Add a TXT record with <code className="bg-red-50 dark:bg-red-900/50 px-1 rounded">v=spf1 include:spf.protection.outlook.com -all</code>
              </p>
            )}
          </div>

          {/* DKIM */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon level={dkimStatus} />
              <span className="text-sm font-medium text-muted-foreground">DKIM (DomainKeys Identified Mail)</span>
            </div>
            {domain.dkim.selector1 || domain.dkim.selector2 ? (
              <div className="space-y-1">
                {domain.dkim.selector1 && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">selector1: </span>
                    <code className="bg-muted px-1 rounded font-mono text-muted-foreground break-all">
                      {domain.dkim.selector1}
                    </code>
                  </div>
                )}
                {domain.dkim.selector2 && (
                  <div className="text-xs">
                    <span className="text-muted-foreground">selector2: </span>
                    <code className="bg-muted px-1 rounded font-mono text-muted-foreground break-all">
                      {domain.dkim.selector2}
                    </code>
                  </div>
                )}
                {!domain.dkim.selector1 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">selector1 CNAME not found</p>
                )}
                {!domain.dkim.selector2 && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400">selector2 CNAME not found</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-red-600 dark:text-red-400">
                No DKIM selectors found. Enable DKIM signing in Microsoft 365 Defender &gt; Email &amp; Collaboration &gt; DKIM.
              </p>
            )}
          </div>

          {/* DMARC */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <StatusIcon level={dmarcStatus} />
              <span className="text-sm font-medium text-muted-foreground">DMARC (Domain-based Message Authentication)</span>
            </div>
            {domain.dmarc.found ? (
              <div>
                <code className="block text-xs bg-muted p-2 rounded font-mono text-muted-foreground break-all">
                  {domain.dmarc.record}
                </code>
                {domain.dmarc.policy === 'none' && (
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    Policy is "none" (monitoring only). Consider upgrading to "quarantine" or "reject" for enforcement.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-sm text-red-600 dark:text-red-400">
                No DMARC record found. Add a TXT record on <code className="bg-red-50 dark:bg-red-900/50 px-1 rounded">_dmarc.{domain.domain}</code> with <code className="bg-red-50 dark:bg-red-900/50 px-1 rounded">v=DMARC1; p=quarantine; rua=mailto:dmarc@{domain.domain}</code>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmailAuthResults({ clientId, onClose }: EmailAuthResultsProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<EmailAuthDomainResult[] | null>(null);
  const [clientName, setClientName] = useState<string>('');

  const handleCheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await checkEmailAuth(clientId);
      if (response.success) {
        setResults(response.domains);
        setClientName(response.clientName);
      } else {
        setError(response.error || 'Failed to check email authentication');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Auto-run on mount
  useEffect(() => {
    handleCheck();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <div className="bg-card shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary-600" />
          <h3 className="text-lg font-medium text-foreground">
            Email Authentication (SPF / DKIM / DMARC)
          </h3>
          {clientName && (
            <span className="text-sm text-muted-foreground">
              — {clientName}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCheck}
            disabled={loading}
            className="text-muted-foreground hover:text-foreground"
            title="Refresh"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {loading && !results && (
          <div className="flex items-center justify-center py-8 gap-2">
            <RefreshCw className="h-5 w-5 animate-spin text-primary-600" />
            <span className="text-sm text-muted-foreground">Checking DNS records...</span>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {results && results.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            No custom domains found for this tenant.
          </p>
        )}

        {results && results.length > 0 && (
          <div className="space-y-3">
            {results.map((domain) => (
              <DomainCard key={domain.domain} domain={domain} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
